/**
 * Three quick visual mockups for the Business AI & Cyber Support page.
 * Mounted at /preview/mockup-a, /preview/mockup-b, /preview/mockup-c.
 * Each shows Hero + first scenarios snippet so the user can pick a direction.
 *
 * Images sourced from Unsplash (royalty-free) and colour-graded via CSS to
 * keep everything on-brand (navy/teal/white/slate).
 */
import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Sparkles, Lock, EyeOff, ShieldCheck, Workflow } from 'lucide-react';
import '../styles/MockupPreviews.css';

const PHOTOS = {
  team:      'https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1600&q=85',
  desk:      'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1600&q=85',
  meeting:   'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=85',
  keyboard:  'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1600&q=85',
  candid:    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1600&q=85',
  portrait:  'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=85',
};

// ============================================================================
// 🅰️  EDITORIAL TRUST — FT Weekend / McKinsey Quarterly / Stripe Press
// ============================================================================
export const MockupA = () => (
  <div className="mk mk-a">
    <header className="mk-a-header">
      <div className="mk-a-mast">
        <span className="mk-a-section-label">All Secured · Capability</span>
        <span className="mk-a-edition">Edition · 01</span>
      </div>
    </header>

    <section className="mk-a-hero">
      <div className="mk-a-hero-grid">
        <div className="mk-a-hero-copy">
          <span className="mk-a-kicker">Practical AI · Cyber safety · For small business</span>
          <h1 className="mk-a-h1">
            Make your business
            <em> safer, sharper</em>
            <br />and more efficient.
          </h1>
          <p className="mk-a-dek">
            All Secured helps businesses implement useful AI workflows, improve digital safety
            habits, and create clearer systems — without drowning teams in jargon.
          </p>
          <div className="mk-a-cta-row">
            <button className="mk-a-cta">Explore scenarios <ArrowRight size={15} /></button>
            <button className="mk-a-cta-ghost">Enquire</button>
          </div>
          <p className="mk-a-byline">By <strong>Nick · All Secured</strong> · Sydney</p>
        </div>

        <figure className="mk-a-hero-figure">
          <div className="mk-a-photo-frame">
            <img src={PHOTOS.team} alt="" />
          </div>
          <figcaption>
            <span className="mk-a-cap-num">01</span>
            Teams using digital tools daily, often without simple rules in place.
          </figcaption>
        </figure>
      </div>

      <blockquote className="mk-a-pullquote">
        “The challenge is not choosing more tools. The challenge is using the right tools
        <em> safely, clearly and efficiently</em>.”
      </blockquote>
    </section>

    <section className="mk-a-scenarios">
      <div className="mk-a-section-head">
        <span className="mk-a-eyebrow">— Three scenarios</span>
        <h2 className="mk-a-h2">See how All Secured would approach the problem.</h2>
      </div>

      <article className="mk-a-article">
        <header>
          <span className="mk-a-tag">Scenario 01 · Professional Services</span>
          <h3>Admin workload is slowing the team down.</h3>
        </header>
        <div className="mk-a-article-body">
          <div>
            <p>
              Your staff repeat manual tasks across email, documents, spreadsheets and follow-up.
              Work gets done, but it depends too heavily on memory.
            </p>
            <p className="mk-a-approach">
              <strong>How we'd approach it.</strong> Map the workflow, identify safe automation
              opportunities, and build a practical AI-assisted process that keeps humans in control.
            </p>
          </div>
          <aside className="mk-a-stack">
            <div className="mk-a-stack-head"><Lock size={12} /> Implementation stack</div>
            {['AI workflow system', 'Secure document assistant', 'Internal knowledge base'].map(s => (
              <div key={s} className="mk-a-stack-row">
                <span>{s}</span>
                <span className="mk-a-blur">████████</span>
              </div>
            ))}
          </aside>
        </div>
      </article>
    </section>
  </div>
);

// ============================================================================
// 🅱️  ATELIER — Linear / Vercel / Stripe.com / Locomotive Studio
// ============================================================================
export const MockupB = () => {
  const heroRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 50, y: 50 });

  return (
    <div className="mk mk-b">
      <div className="mk-b-aurora" />
      <div className="mk-b-aurora mk-b-aurora-2" />

      <header className="mk-b-header">
        <div className="mk-b-brand">
          <span className="mk-b-dot" />
          All Secured <span className="mk-b-brand-sub">/ business</span>
        </div>
        <button className="mk-b-pill">Enquire <ArrowRight size={13} /></button>
      </header>

      <section
        ref={heroRef}
        className="mk-b-hero"
        onMouseMove={(e) => {
          const r = heroRef.current.getBoundingClientRect();
          setMouse({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
        }}
        style={{ '--mx': `${mouse.x}%`, '--my': `${mouse.y}%` }}
      >
        <div className="mk-b-spotlight" />

        <div className="mk-b-hero-inner">
          <span className="mk-b-chip"><span className="mk-b-pulse" /> Now accepting Q2 enquiries</span>
          <h1 className="mk-b-h1">
            Make your business <span className="mk-b-grad">safer, sharper</span> and more efficient.
          </h1>
          <p className="mk-b-sub">
            Practical AI workflows. Calm cyber safety. Clearer systems. Built for small teams
            who want momentum, not theory.
          </p>
          <div className="mk-b-cta-row">
            <button className="mk-b-cta">Explore scenarios <ArrowRight size={15} /></button>
            <button className="mk-b-cta-ghost">See approach</button>
          </div>

          <div className="mk-b-stats">
            {[
              { v: '4', l: 'days · diagnostic' },
              { v: '12+', l: 'workflows mapped' },
              { v: '0', l: 'jargon, no scare tactics' },
            ].map(s => (
              <div key={s.l} className="mk-b-stat">
                <div className="mk-b-stat-v">{s.v}</div>
                <div className="mk-b-stat-l">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mk-b-photo-stack">
          <div className="mk-b-photo mk-b-photo-1" style={{ backgroundImage: `url(${PHOTOS.keyboard})` }} />
          <div className="mk-b-photo mk-b-photo-2" style={{ backgroundImage: `url(${PHOTOS.desk})` }} />
          <div className="mk-b-glass-card">
            <div className="mk-b-glass-label">Business Safety Snapshot · Preview</div>
            <div className="mk-b-glass-row"><span>AI use</span><span className="mk-b-amber">Unclear rules</span></div>
            <div className="mk-b-glass-row"><span>Passwords</span><span className="mk-b-amber">Mixed habits</span></div>
            <div className="mk-b-glass-row"><span>Marketing funnel</span><span className="mk-b-amber">Tracking gap</span></div>
          </div>
        </div>
      </section>

      <section className="mk-b-scenarios">
        <span className="mk-b-eyebrow">Approach</span>
        <h2 className="mk-b-h2">See how we'd think through your situation.</h2>

        <div className="mk-b-scenario-card">
          <div className="mk-b-scenario-photo" style={{ backgroundImage: `url(${PHOTOS.candid})` }}>
            <div className="mk-b-scenario-overlay" />
            <div className="mk-b-scenario-tag">Scenario 01 · Admin Efficiency</div>
          </div>
          <div className="mk-b-scenario-body">
            <h3>Admin workload is slowing the team down</h3>
            <p>Your staff are repeating tasks across email, docs and follow-up. Work gets done — but depends too heavily on memory.</p>
            <div className="mk-b-stack">
              {[
                { icon: Workflow, label: 'AI workflow system' },
                { icon: ShieldCheck, label: 'Secure document assistant' },
              ].map(({ icon: Ico, label }) => (
                <div key={label} className="mk-b-stack-card">
                  <div className="mk-b-stack-ico"><Ico size={14} /></div>
                  <div>
                    <div className="mk-b-stack-label">{label}</div>
                    <div className="mk-b-stack-blur">████████████</div>
                  </div>
                  <button className="mk-b-unlock"><EyeOff size={11} /> Unlock</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============================================================================
// 🅲  BOARDROOM BRIEFING — Bloomberg / Palantir / Apple Business
// ============================================================================
export const MockupC = () => {
  const [count, setCount] = useState({ a: 0, b: 0, c: 0 });

  useEffect(() => {
    const targets = { a: 4, b: 12, c: 0 };
    const step = () => {
      setCount(prev => ({
        a: prev.a < targets.a ? prev.a + 1 : prev.a,
        b: prev.b < targets.b ? prev.b + 1 : prev.b,
        c: prev.c,
      }));
    };
    const id = setInterval(step, 110);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="mk mk-c">
      <div className="mk-c-grid-bg" />

      <header className="mk-c-header">
        <div className="mk-c-brand-row">
          <span className="mk-c-brand">ALL SECURED</span>
          <span className="mk-c-divider">/</span>
          <span className="mk-c-section">Business Capability Brief</span>
        </div>
        <div className="mk-c-meta">
          <span className="mk-c-live"><span className="mk-c-live-dot" /> Available</span>
          <span className="mk-c-mono">SYD · AU</span>
        </div>
      </header>

      <section className="mk-c-hero">
        <div className="mk-c-hero-left">
          <div className="mk-c-ref">REF · BIZ-CAP-2026</div>
          <h1 className="mk-c-h1">
            Make your business safer,<br />sharper and more efficient.
          </h1>
          <p className="mk-c-lede">
            Practical AI implementation, cyber safety baseline and digital systems review
            for small businesses that want measurable outcomes.
          </p>

          <div className="mk-c-data-row">
            <div className="mk-c-data">
              <div className="mk-c-data-num">{count.a}<span>days</span></div>
              <div className="mk-c-data-l">Initial diagnostic</div>
            </div>
            <div className="mk-c-data">
              <div className="mk-c-data-num">{count.b}<span>+</span></div>
              <div className="mk-c-data-l">Workflows mapped</div>
            </div>
            <div className="mk-c-data">
              <div className="mk-c-data-num">{count.c}<span>%</span></div>
              <div className="mk-c-data-l">Jargon delivered</div>
            </div>
          </div>

          <div className="mk-c-cta-row">
            <button className="mk-c-cta">Open scenarios <ArrowRight size={14} /></button>
            <button className="mk-c-cta-ghost">Send brief</button>
          </div>
        </div>

        <aside className="mk-c-hero-right">
          <div className="mk-c-inset-photo">
            <img src={PHOTOS.portrait} alt="" />
            <div className="mk-c-inset-overlay" />
            <div className="mk-c-inset-tag">PRINCIPAL · NICK</div>
          </div>

          <div className="mk-c-audit-card">
            <div className="mk-c-audit-head">
              <span className="mk-c-mono mk-c-audit-label">BUSINESS_SAFETY_SNAPSHOT</span>
              <span className="mk-c-audit-state">PREVIEW</span>
            </div>
            {[
              ['AI use', 'Unclear rules', 'amber'],
              ['Passwords', 'Mixed habits', 'amber'],
              ['Customer data', 'Needs review', 'slate'],
              ['Marketing funnel', 'Tracking gap', 'amber'],
            ].map(([k, v, c]) => (
              <div key={k} className="mk-c-audit-row">
                <span className="mk-c-mono">{k}</span>
                <span className={`mk-c-audit-state-v mk-c-${c}`}>{v}</span>
              </div>
            ))}
          </div>
        </aside>
      </section>

      <section className="mk-c-scenarios">
        <div className="mk-c-section-head">
          <span className="mk-c-mono mk-c-section-mono">§ SCENARIOS</span>
          <h2 className="mk-c-h2">How All Secured would approach the problem.</h2>
        </div>

        <div className="mk-c-scenario-row">
          <div className="mk-c-scenario-narr">
            <div className="mk-c-scenario-tag">SC-01 · ADMIN EFFICIENCY</div>
            <h3>Admin workload is slowing the team down</h3>
            <p>Manual tasks repeat across email, documents and follow-up. The work gets done — but it takes too long and depends on memory.</p>
            <ol>
              <li><span>01</span> Document current workflow + friction points</li>
              <li><span>02</span> Identify safe AI use cases with low risk</li>
              <li><span>03</span> Build prompt templates + escalation rules</li>
              <li><span>04</span> Train team on what to automate (and what not to)</li>
            </ol>
          </div>
          <div className="mk-c-scenario-data">
            <div className="mk-c-data-card">
              <div className="mk-c-mono mk-c-data-card-label">STACK · PROTECTED</div>
              {['AI workflow system', 'Secure document assistant', 'Internal knowledge base', 'Automation connector'].map(s => (
                <div key={s} className="mk-c-data-card-row">
                  <span>{s}</span>
                  <span className="mk-c-blur"><Lock size={10} /></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
